import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { CONFIG } from 'src/global-config';

import { AUTH } from './firebase';
import { recordBackendFailure } from './error-reporter';
import { getDebugSurface } from './dev-telemetry';

// ----------------------------------------------------------------------

// Storage key for auth token
const FIREBASE_TOKEN_STORAGE_KEY = 'firebase_auth_token';
const FIREBASE_TOKEN_EXPIRY_KEY = 'firebase_auth_token_expiry';

// Token management variables
let authToken: string | null = null;
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

// The signed-in user used for token refresh. On web this is `AUTH.currentUser`,
// but native (Capacitor) phone auth does NOT populate the Web SDK's currentUser —
// it signs in via the FirebaseAuthentication plugin and `setAuthToken` is called
// with a plain "userLike" object. We track that here so the refresh timer / 401
// handler / request interceptor can refresh native sessions too (otherwise the
// token is cleared at expiry and every request 401s with "Missing auth token").
let currentAuthUser: any = null;
const getAuthUser = (): any => AUTH.currentUser ?? currentAuthUser;

// Helper to check if we're in browser environment
const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

// Decode a Firebase ID token (JWT) and return its expiration in ms since epoch.
// Used instead of user.getIdTokenResult() so we don't depend on the Firebase User
// method surface - the Capacitor native auth path supplies a plain user object.
export const getJwtExpirationMs = (jwt: string): number => {
  const [, payload] = jwt.split('.');
  if (!payload) throw new Error('Invalid ID token');
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (normalized.length % 4)) % 4;
  const b64 = normalized + '='.repeat(padLen);
  const json = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('utf8');
  const { exp } = JSON.parse(json) as { exp: number };
  return exp * 1000;
};

// Helper function to check if stored token is valid
const isStoredTokenValid = () => {
  if (!isBrowser) return false;

  const storedToken = localStorage.getItem(FIREBASE_TOKEN_STORAGE_KEY);
  const expiryStr = localStorage.getItem(FIREBASE_TOKEN_EXPIRY_KEY);

  if (!storedToken || !expiryStr) {
    return false;
  }

  const expiry = parseInt(expiryStr, 10);
  const now = Date.now();

  // Token is valid if it expires more than 5 minutes from now
  return expiry > now + 5 * 60 * 1000;
};

// Initialize token from storage
const initializeFromStorage = () => {
  if (!isBrowser) return;

  if (isStoredTokenValid()) {
    const storedToken = localStorage.getItem(FIREBASE_TOKEN_STORAGE_KEY);
    const expiryStr = localStorage.getItem(FIREBASE_TOKEN_EXPIRY_KEY);
    const expiry = parseInt(expiryStr!, 10);
    const now = Date.now();

    authToken = storedToken;
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${authToken}`;

    // Schedule refresh
    const timeToRefresh = expiry - now - 10 * 60 * 1000; // 10 min buffer
    if (timeToRefresh > 0) {
      refreshTimerId = setTimeout(() => {
        // By the time this runs, the signed-in user should be available.
        const user = getAuthUser();
        if (user) {
          refreshAuthToken(user);
        } else {
          // Token is about to expire but no user - clear it
          setAuthToken(null);
        }
      }, timeToRefresh);
    }
  } else {
    // Token is expired or invalid, clear it
    if (isBrowser) {
      localStorage.removeItem(FIREBASE_TOKEN_STORAGE_KEY);
      localStorage.removeItem(FIREBASE_TOKEN_EXPIRY_KEY);
    }
    authToken = null;
  }
};

// Function to set the auth token and schedule refresh
export const setAuthToken = async (user: any) => {
  // Remember the active user (web User or native userLike) so refresh paths that
  // can't see `AUTH.currentUser` (native) can still get a fresh token.
  currentAuthUser = user ?? null;
  if (!user) {
    authToken = null;
    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
    delete axiosInstance.defaults.headers.common.Authorization;
    if (isBrowser) {
      localStorage.removeItem(FIREBASE_TOKEN_STORAGE_KEY);
      localStorage.removeItem(FIREBASE_TOKEN_EXPIRY_KEY);
    }
    return;
  }

  try {
    // Get fresh token (force refresh)
    const freshToken = await user.getIdToken(true);
    authToken = freshToken;

    // Set the token in axios headers
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${freshToken}`;

    // Get token expiration (Firebase tokens expire in 1 hour by default).
    // Decode the JWT directly so this works for both Firebase Web SDK users and
    // the Capacitor native userLike object (which has no getIdTokenResult).
    const expirationTime = getJwtExpirationMs(freshToken);
    const currentTime = Date.now();

    // Store token and expiry in localStorage
    if (authToken && isBrowser) {
      localStorage.setItem(FIREBASE_TOKEN_STORAGE_KEY, authToken);
      localStorage.setItem(FIREBASE_TOKEN_EXPIRY_KEY, expirationTime.toString());
    }

    // Schedule refresh 10 minutes before expiration (more buffer time)
    const timeToRefresh = expirationTime - currentTime - 10 * 60 * 1000;

    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
    }

    if (timeToRefresh > 0) {
      refreshTimerId = setTimeout(() => refreshAuthToken(user), timeToRefresh);
      console.log(`Token refresh scheduled in ${Math.round(timeToRefresh / 60000)} minutes`);
    } else {
      // Token expires very soon, refresh immediately
      console.log('Token expires soon, refreshing immediately');
      setTimeout(() => refreshAuthToken(user), 1000);
    }
  } catch (error) {
    console.error('Error setting auth token:', error);
    throw error;
  }
};

// Function to refresh the token
export const refreshAuthToken = async (user: any) => {
  if (!user) return Promise.resolve();

  // Prevent multiple simultaneous refresh attempts
  if (isRefreshing) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      console.log('Refreshing auth token...');
      await setAuthToken(user);
      console.log('Auth token refreshed successfully');
    } catch (error) {
      console.error('Error refreshing auth token:', error);
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Utility function to check if current token is valid
export const getTokenInfo = async () => {
  if (!AUTH.currentUser) {
    return { valid: false, reason: 'No authenticated user' };
  }

  try {
    const token = await AUTH.currentUser.getIdToken(false);
    const expirationTime = getJwtExpirationMs(token);
    const currentTime = Date.now();
    const minutesUntilExpiry = Math.round((expirationTime - currentTime) / 60000);

    return {
      valid: true,
      token: token.substring(0, 20) + '...', // Only show first 20 chars for security
      expiresIn: minutesUntilExpiry,
      expirationTime: new Date(expirationTime).toISOString(),
    };
  } catch (error: any) {
    return { valid: false, reason: error.message };
  }
};

const axiosInstance = axios.create({
  baseURL: CONFIG.serverUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Initialize token from storage before any requests are made
initializeFromStorage();

// Stable random UUID v4 for Idempotency-Key. Falls back for older browsers.
const mintIdempotencyKey = (): string => {
  const g: any = typeof globalThis !== 'undefined' ? globalThis : {};
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  // Fallback: 16 random bytes → hex, formatted as UUID v4.
  const bytes = new Uint8Array(16);
  (
    g.crypto?.getRandomValues?.bind(g.crypto) ??
    ((b: Uint8Array) => {
      for (let i = 0; i < b.length; i += 1) b[i] = Math.floor(Math.random() * 256);
      return b;
    })
  )(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const IDEMPOTENT_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Fetch a Firebase ID token, retrying once on a transient failure. Right after
// the app resumes from background the network is often not reconnected yet, so
// the first getIdToken() call can reject; a brief retry (forcing a fresh fetch)
// usually succeeds once connectivity is back.
const getIdTokenWithRetry = async (user: any): Promise<string> => {
  try {
    return await user.getIdToken(false); // use the cached token if still valid
  } catch (error) {
    console.warn('[axios] getIdToken failed, retrying once:', (error as Error)?.message);
    await new Promise((resolve) => setTimeout(resolve, 800));
    return user.getIdToken(true); // force a fresh fetch on the retry
  }
};

// Add auth token and Idempotency-Key to requests
axiosInstance.interceptors.request.use(
  async (config) => {
    // If a token refresh is already in flight (e.g. kicked off by the app-resume
    // handler), wait for it so we don't read a stale/absent token mid-refresh.
    if (isRefreshing && refreshPromise) {
      try {
        await refreshPromise;
      } catch {
        /* fall through - the per-request fetch below will retry */
      }
    }

    const authUser = getAuthUser();
    try {
      if (authUser) {
        // Always get a fresh token to avoid expired token issues
        const token = await getIdTokenWithRetry(authUser);
        config.headers.Authorization = `Bearer ${token}`;
        // Update our stored token if it's different
        if (token !== authToken) {
          authToken = token;
        }
      } else if (authToken && isStoredTokenValid()) {
        // Only use stored token if it's still valid
        config.headers.Authorization = `Bearer ${authToken}`;
      } else {
        // Clear invalid stored token
        if (authToken && !isStoredTokenValid()) {
          authToken = null;
          if (isBrowser) {
            localStorage.removeItem(FIREBASE_TOKEN_STORAGE_KEY);
            localStorage.removeItem(FIREBASE_TOKEN_EXPIRY_KEY);
          }
        }
        // Don't add authorization header if no valid token
        delete config.headers.Authorization;
      }
    } catch (error) {
      console.error('Error getting auth token for request:', error);
      if (authUser) {
        // There IS a signed-in user but the token could not be fetched (token
        // refresh genuinely failed, e.g. network still down on resume). Do NOT
        // send the request without a header - that just yields a confusing
        // "missing authorization token" 401. Reject so SWR / the caller retries
        // once connectivity is restored.
        return Promise.reject(
          new axios.Cancel('Auth token unavailable - request not sent (will retry)')
        );
      }
      // No signed-in user - send unauthenticated (public endpoints still work).
      delete config.headers.Authorization;
    }

    // Attach Idempotency-Key to mutations so the backend `idempotent()` middleware
    // can dedupe retried requests. Re-uses the same key across retries of the
    // same axios request (axios copies `config` on retry, preserving the header).
    const method = (config.method || 'get').toLowerCase();
    if (IDEMPOTENT_METHODS.has(method)) {
      const headers = config.headers ?? {};
      if (!headers['Idempotency-Key'] && !headers['idempotency-key']) {
        headers['Idempotency-Key'] = mintIdempotencyKey();
        config.headers = headers;
      }
    }

    // Dev-only: label which UI surface fired this call so `yarn devlog` (apps/api)
    // can attribute it. Stripped from production bundles by the import.meta.env.DEV guard.
    if (import.meta.env.DEV) {
      const headers = config.headers ?? {};
      headers['X-Debug-Surface'] = getDebugSurface();
      config.headers = headers;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (authentication failed)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = getAuthUser();
        if (user) {
          console.log('Token expired, refreshing...');

          // Use the race-condition-safe refresh function
          await refreshAuthToken(user);

          // refreshAuthToken → setAuthToken just stored the fresh token; reuse it
          // (works for native userLike too, without another plugin round-trip).
          if (authToken) {
            originalRequest.headers.Authorization = `Bearer ${authToken}`;
          }

          // Retry the original request
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.warn(
          '[axios] token refresh failed:',
          (refreshError as Error)?.message ?? refreshError
        );
        // Clear invalid token
        setAuthToken(null);
        // Redirect to login or handle as needed
        window.location.href = '/auth/sign-in';
        return Promise.reject(refreshError);
      }
    }

    recordBackendFailure(error);

    // The API answers every failure with { success: false, error, code }, but
    // axios's own message is "Request failed with status code 422" — which is
    // what each screen ends up putting in front of the agent, since they render
    // `err.message`. Promote the server's message so the actionable text wins.
    // Mutating `message` keeps the AxiosError shape intact (see below).
    const body = error.response?.data;
    const serverMessage =
      body && typeof body === 'object' ? (body as { error?: unknown }).error : undefined;
    if (typeof serverMessage === 'string' && serverMessage.trim()) {
      error.message = serverMessage;
    }

    // Preserve the AxiosError so callers retain status, response, stack, and config.
    // Previously this rejected with a bare string ("Something went wrong!"), which
    // destroyed the error shape and produced unhelpful console traces.
    return Promise.reject(error);
  }
);

// ----------------------------------------------------------------------

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    console.log('[FETCHER] 🌐 Making API call:', { url, params: config?.params });
    const res = await axiosInstance.get<T>(url, config);
    console.log('[FETCHER] ✅ API call successful:', { url, dataReceived: !!res.data });

    return res.data;
  } catch (error) {
    console.error('[FETCHER] ❌ Failed to fetch:', {
      url: Array.isArray(args) ? args[0] : args,
      error,
    });
    throw error;
  }
};

// ----------------------------------------------------------------------

// NOTE (2026-04-19 sync audit): entries tagged @deprecated-404 point at routes
// the backend does not currently register; they are retained so existing call
// sites compile, but they will 404 in production. Track removal or backend
// implementation per the plan in
// ~/.claude/plans/users-hansrajpatel-dev-personal-fintech-sequential-glacier.md.
export const endpoints = {
  auth: {
    me: '/auth/me',
    // Current-user profile is surfaced via /auth/me; updates go through /users/:uid.
    profile: '/auth/me',
    /** @deprecated-404 no backend logout route; use Firebase client signOut() */
    logout: '/auth/logout',
  },
  upload: {
    image: '/upload/image',
  },
  users: {
    list: '/users',
    details: (uid: string) => `/users/${uid}`,
    /** @deprecated legacy alias - replaced by kyc.queue */
    pendingKYC: '/kyc/queue',
    stats: '/users/stats',
    invite: '/users/invite',
    bulkInvite: '/users/bulk-invite',
    updateProfile: (uid: string) => `/users/${uid}`,
    /** @deprecated-404 no backend route; admin status updates not yet wired */
    updateStatus: (uid: string) => `/users/${uid}/status`,
    // Backend: PATCH /users/:uid/role (ADMIN only). Promote/demote a user.
    updateRole: (uid: string) => `/users/${uid}/role`,
    // Backend: GET /users/:uid/beneficiaries (ADMIN, REVIEWER). A user's senders.
    beneficiaries: (uid: string) => `/users/${uid}/beneficiaries`,
    // Backend: GET /users/:uid/activity (ADMIN, REVIEWER). Audit footprint.
    activity: (uid: string) => `/users/${uid}/activity`,
    // Backend: PATCH /users/:uid/kyc-documents (ADMIN, REVIEWER, or self).
    // Persists fresh KYC document URLs onto the user's profile metadata.
    updateKYCDocuments: (uid: string) => `/users/${uid}/kyc-documents`,
    /** @deprecated use `kyc.approve` instead */
    approveKYC: (uid: string) => `/kyc/${uid}/approve`,
    /** @deprecated use `kyc.reject` instead */
    rejectKYC: (uid: string) => `/kyc/${uid}/reject`,
    /** @deprecated-404 no backend route */
    disable: (uid: string) => `/users/${uid}/disable`,
  },
  kyc: {
    queue: '/kyc/queue',
    documents: (uid: string) => `/kyc/${uid}/documents`,
    rejectionReasons: (uid: string) => `/kyc/${uid}/rejection-reasons`,
    approve: (uid: string) => `/kyc/${uid}/approve`,
    reject: (uid: string) => `/kyc/${uid}/reject`,
    resubmit: (uid: string) => `/kyc/${uid}/resubmit`,
    claim: (uid: string) => `/kyc/${uid}/claim`,
  },
  dealers: {
    /** @deprecated-404 no backend route; use dealers.invite */
    create: '/users/admin/create',
    invite: '/users/distributor/invite-dealer',
    update: (uid: string) => `/users/${uid}`,
  },
  distributors: {
    /** @deprecated-404 no backend route */
    create: '/users/admin/create',
    update: (uid: string) => `/users/${uid}`,
  },
  beneficiaries: {
    list: '/beneficiaries',
    create: '/beneficiaries',
    details: (id: string) => `/beneficiaries/${id}`,
    update: (id: string) => `/beneficiaries/${id}`,
    delete: (id: string) => `/beneficiaries/${id}`,
    favorites: '/beneficiaries/favorites',
    recent: '/beneficiaries/recent',
    search: '/beneficiaries/search',
    // Backend: GET /beneficiaries/:id/activity (owner or ADMIN). Sender audit trail.
    activity: (id: string) => `/beneficiaries/${id}/activity`,
  },
  paymentMethods: {
    list: '/payment-methods',
    create: '/payment-methods',
    details: (id: string) => `/payment-methods/${id}`,
    update: (id: string) => `/payment-methods/${id}`,
    delete: (id: string) => `/payment-methods/${id}`,
    validateVPA: '/payment-methods/validate-vpa',
    validateBank: (id: string) => `/payment-methods/${id}/validate-bank`,
    replace: (id: string) => `/payment-methods/${id}/replace`,
    setDefault: (id: string) => `/payment-methods/${id}/set-default`,
    listByBeneficiary: (beneficiaryId: string) => `/beneficiaries/${beneficiaryId}/payment-methods`,
  },
  transactions: {
    list: '/transactions',
    details: (id: string) => `/transactions/${id}`,
    dmt: '/transactions/dmt',
    /** Fino only — request the per-transaction sender OTP before /transactions/dmt. */
    dmtOtp: '/transactions/dmt/otp',
    cancel: (id: string) => `/transactions/${id}/cancel`,
    retry: (id: string) => `/transactions/${id}/retry`,
    /** @deprecated-404 txn type dropped; use `/collections` instead */
    cashCollection: '/transactions/cash-collection',
    /** @deprecated-404 txn type dropped; use `/collections` instead */
    cashDeposit: '/transactions/cash-deposit',
    /** @deprecated-404 no backend route */
    markPickedUp: (id: string) => `/transactions/${id}/mark-picked-up`,
    /** @deprecated-404 no backend route */
    markReceived: (id: string) => `/transactions/${id}/mark-received`,
    /** @deprecated-404 no backend route */
    approve: (id: string) => `/transactions/${id}/approve`,
    /** @deprecated-404 no backend route */
    reject: (id: string) => `/transactions/${id}/reject`,
    /** @deprecated-404 no backend route; backend polls Eko on a cron */
    syncStatus: (id: string) => `/transactions/${id}/sync-status`,
    /** @deprecated-404 no backend route */
    pendingApproval: '/transactions/pending-approval',
    /** @deprecated-404 no backend route */
    analytics: '/transactions/analytics',
    /** @deprecated-404 no backend route */
    adminAnalytics: '/transactions/admin/analytics',
  },
  credit: {
    /** @deprecated-404 no backend route; use `credit.details(dealerId)` with session dealerId */
    balance: '/credit/balance',
    details: (dealerId: string) => `/credit/${dealerId}`,
    available: (dealerId: string) => `/credit/${dealerId}/available`,
    usage: (dealerId: string) => `/credit/${dealerId}/usage`,
    requestExtension: '/credit/request-extension',
    requests: '/credit/extension-requests',
    requestDetails: (id: string) => `/credit/requests/${id}`,
    approveRequest: (id: string) => `/credit/requests/${id}/approve`,
    rejectRequest: (id: string) => `/credit/requests/${id}/reject`,
    updateLimit: (dealerId: string) => `/credit/${dealerId}/limit`,
    updateStatus: (dealerId: string) => `/credit/${dealerId}/status`,
    /** @deprecated-404 no backend route */
    stats: '/credit/stats',
    /** @deprecated-404 no backend route */
    pendingCollections: '/credit/pending-collections',
    /** @deprecated-404 collections moved to `POST /collections` */
    collect: (dealerId: string) => `/credit/${dealerId}/collect`,
    distributor: {
      /** @deprecated-404 no backend route */
      get: (distributorId: string) => `/credit/distributor/${distributorId}`,
      /** @deprecated-404 no backend route */
      updateLimit: (distributorId: string) => `/credit/distributor/${distributorId}/limit`,
    },
  },
  collections: {
    list: '/collections',
    create: '/collections',
    /** @deprecated-404 legacy admin ledger API never existed on current backend */
    distributorStats: '/collections/distributor-stats',
    /** @deprecated-404 */
    distributorLedger: '/collections/distributor-ledger',
    /** @deprecated-404 */
    adminDistributorsPending: '/collections/admin/distributors-pending',
    /** @deprecated-404 */
    adminDistributorStats: (distributorId: string) =>
      `/collections/admin/distributor-stats/${distributorId}`,
    /** @deprecated-404 */
    adminDistributorLedger: (distributorId: string) =>
      `/collections/admin/distributor-ledger/${distributorId}`,
    /** @deprecated-404 */
    adminDistributorCollectionHistory: (distributorId: string) =>
      `/collections/admin/distributor-collection-history/${distributorId}`,
    /** @deprecated-404 use `/collections` */
    adminRecordDeposit: '/collections/admin/record-deposit',
    /** @deprecated-404 */
    adminDealersPending: '/collections/admin/dealers-pending',
    /** @deprecated-404 */
    adminDealerStats: (dealerId: string) => `/collections/admin/dealer-stats/${dealerId}`,
    /** @deprecated-404 */
    adminDealerLedger: (dealerId: string) => `/collections/admin/dealer-ledger/${dealerId}`,
    /** @deprecated-404 */
    adminDealerCollectionHistory: (dealerId: string) =>
      `/collections/admin/dealer-collection-history/${dealerId}`,
    /** @deprecated-404 use `/collections` */
    adminRecordAdvance: '/collections/admin/record-advance',
  },
  notifications: {
    list: '/notifications',
    unreadCount: '/notifications/unread-count',
    markRead: (id: string) => `/notifications/${id}/read`,
    markAllRead: '/notifications/mark-all-read',
    delete: (id: string) => `/notifications/${id}`,
    test: '/notifications/test',
    preferences: '/notifications/preferences',
    whatsappOptIn: '/notifications/whatsapp/opt-in',
  },
  commissions: {
    list: '/commissions',
    hierarchy: (userId: string) => `/commissions/hierarchy/${userId}`,
    hierarchyCreate: '/commissions/hierarchy',
    hierarchyUpdate: (id: string) => `/commissions/hierarchy/${id}`,
    /** @deprecated use commissions.hierarchy - backend has no `/commissions/:childId` route */
    get: (childId: string) => `/commissions/hierarchy/${childId}`,
    /** @deprecated use commissions.hierarchyUpdate */
    set: (childId: string) => `/commissions/hierarchy/${childId}`,
    /** @deprecated-404 no backend route */
    children: '/commissions/children',
    /** @deprecated-404 no backend route; see plan m06 */
    summary: '/commissions/summary',
    /** @deprecated-404 no backend route; see plan m06 */
    transactions: '/commissions/transactions',
  },
  dmtSenders: {
    profile: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/profile`,
    onboard: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/onboard`,
    /** Fino only — verify the customer-mobile OTP that completes platform registration. */
    customerOtp: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/customer-otp`,
    generateAadhaarOtp: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/aadhaar-otp`,
    validateAadhaarOtp: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/aadhaar-otp`,
    biometricEkyc: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/biometric-ekyc`,
    /** Fino only — validate the eKYC OTP after biometric capture. */
    ekycOtp: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/ekyc-otp`,
    /** iOS Face-auth eKYC (Fino): start an AadhaarFaceRD headless capture session. */
    facerdSession: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/facerd/session`,
    /** iOS Face-auth eKYC: poll the async FaceRD result by txnId. */
    facerdStatus: (beneficiaryId: string, txnId: string) =>
      `/dmt/senders/${beneficiaryId}/facerd/status?txnId=${encodeURIComponent(txnId)}`,
    status: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/status`,
    refreshTier: (beneficiaryId: string) => `/dmt/senders/${beneficiaryId}/refresh-tier`,
  },
  transactionLimits: {
    get: (dealerId: string) => `/transaction-limits/${dealerId}`,
    check: (dealerId: string) => `/transaction-limits/${dealerId}/check`,
    /** @deprecated-404 not built on backend */
    usage: (dealerId: string) => `/transaction-limits/${dealerId}/usage`,
  },
  fcm: {
    tokens: '/fcm/tokens',
    tokenById: (tokenOrId: string) => `/fcm/tokens/${tokenOrId}`,
    /** @deprecated use `fcm.tokens` */
    register: '/fcm/tokens',
    /** @deprecated no-op - registration auto-links to the authed user */
    link: '/fcm/tokens',
    /** @deprecated use `fcm.tokenById(token)` with DELETE */
    deactivate: '/fcm/tokens',
  },
  grievances: {
    list: '/grievances',
    create: '/grievances',
    details: (id: string) => `/grievances/${id}`,
    comment: (id: string) => `/grievances/${id}/comment`,
    assign: (id: string) => `/grievances/${id}/assign`,
    resolve: (id: string) => `/grievances/${id}/resolve`,
    claim: (id: string) => `/grievances/${id}/claim`,
  },
  admin: {
    auditLog: '/admin/audit-log',
    errorLog: '/admin/error-log',
    ekoLog: '/admin/eko-log',
    ekoLogExport: '/admin/eko-log/export',
    nodalOfficer: '/admin/nodal-officer',
    seed: '/admin/seed',
  },
  consents: {
    list: '/consents',
    capture: '/consents',
    revoke: (type: string) => `/consents/${type}`,
  },
  dpdpa: {
    dataExport: '/users/me/data-export',
    dataExportStatus: (requestId: string) => `/users/me/data-export/${requestId}`,
    erasure: '/users/me/data',
  },
  ledger: {
    accounts: '/ledger/accounts',
    accountBalance: (id: string) => `/ledger/accounts/${id}/balance`,
    accountEntries: (id: string) => `/ledger/accounts/${id}/entries`,
    entries: '/ledger/entries',
  },
  disputes: {
    list: '/disputes',
    create: '/disputes',
    details: (id: string) => `/disputes/${id}`,
    decide: (id: string) => `/disputes/${id}/decide`,
    chargebackList: '/disputes/chargebacks',
    chargebackCreate: '/disputes/chargeback',
  },
  commissionPromos: {
    list: '/commissions/promos',
    create: '/commissions/promos',
    update: (id: string) => `/commissions/promos/${id}`,
    delete: (id: string) => `/commissions/promos/${id}`,
  },
  payout: {
    health: '/payout/health',
  },
  geofence: {
    zones: '/geofence/zones',
    zone: (id: string) => `/geofence/zones/${id}`,
    zoneUsers: (id: string) => `/geofence/zones/${id}/users`,
    zoneUser: (id: string, uid: string) => `/geofence/zones/${id}/users/${uid}`,
  },
  notificationsAdmin: {
    templates: '/notifications/templates',
    template: (key: string) => `/notifications/templates/${key}`,
    testSend: '/notifications/test-send',
  },
  adminTransactions: {
    awaitingApproval: '/transactions/awaiting-approval',
    pendingApproval: '/transactions/pending-approval',
    stuck: '/transactions/stuck',
    approve: (id: string) => `/transactions/${id}/approve`,
    reject: (id: string) => `/transactions/${id}/reject`,
    syncStatus: (id: string) => `/transactions/${id}/sync-status`,
  },
  settlement: {
    daily: '/settlement/daily',
    weekly: '/settlement/weekly',
    closeDay: (date: string) => `/settlement/daily/${date}/close`,
    reopenDay: (date: string) => `/settlement/daily/${date}/reopen`,
  },
  reconciliation: {
    unmatched: '/admin/reconciliation/unmatched',
    matches: '/admin/reconciliation/matches',
    match: '/admin/reconciliation/match',
    writeOff: '/admin/reconciliation/write-off',
  },
  variance: {
    summary: '/admin/variance/summary',
    cycle: (cycleId: string) => `/admin/variance/cycles/${cycleId}`,
  },
  float: {
    get: '/admin/float',
    probe: '/admin/float/probe',
    topUp: '/admin/float/top-up',
  },
  ota: {
    releases: '/ota/releases',
    active: '/ota/releases/active',
    release: (id: string) => `/ota/releases/${id}`,
    uploadUrl: '/ota/releases/upload-url',
    uploadBundle: '/ota/releases/upload-bundle',
    stage: (id: string) => `/ota/releases/${id}/stage`,
    rollout: (id: string) => `/ota/releases/${id}/rollout`,
    halt: (id: string) => `/ota/releases/${id}/halt`,
    rollback: (id: string) => `/ota/releases/${id}/rollback`,
  },
  reports: {
    gmv: '/reports/gmv',
    commissionPaid: '/reports/commission-paid',
    disputeVolume: '/reports/dispute-volume',
    settlement: '/reports/settlement',
  },
  commissionsAdmin: {
    tiers: '/commissions/tiers',
    payouts: '/commissions/payouts',
    payout: (id: string) => `/commissions/payouts/${id}`,
    approvePayout: (id: string) => `/commissions/payouts/${id}/approve`,
    executePayout: (id: string) => `/commissions/payouts/${id}/execute`,
    retryPayoutItem: (batchId: string, itemId: string) =>
      `/commissions/payouts/${batchId}/items/${itemId}/retry`,
    tds: '/commissions/tds',
  },
  opsExports: {
    list: '/admin/ops/exports',
    detail: (id: string) => `/admin/ops/exports/${id}`,
    create: '/admin/ops/exports',
    cancel: (id: string) => `/admin/ops/exports/${id}/cancel`,
    download: (id: string) => `/admin/ops/exports/${id}/download`,
  },
} as const;
